/**
 * Form validation utilities
 * Centralizes validation functions for consistent form validation across the application
 */

import { ValidationError } from "class-validator";

/**
 * Validation result type
 */
export interface ValidationResult {
	isValid: boolean;
	error?: ValidationError;
}

/**
 * Validate password format
 * Password must contain at least 8 characters with 1 uppercase, 1 lowercase, and 1 digit
 *
 * @param password - Password to validate
 * @returns Validation result with error if invalid
 */
export function validatePassword(password: string): ValidationResult {
	const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?&_#\\.\\-]{8,}$/;
	if (!passwordRegex.test(password)) {
		return {
			isValid: false,
			error: {
				property: "password",
				constraints: {
					"400": "Le mot de passe doit contenir au moins 8 caractères dont 1 majuscule, 1 minuscule et 1 chiffre.",
				},
				children: [],
			},
		};
	}
	return { isValid: true };
}

/**
 * Validate email format
 *
 * @param email - Email to validate
 * @returns Validation result with error if invalid
 */
export function validateEmail(email: string): ValidationResult {
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	if (!email || !emailRegex.test(email.trim())) {
		return {
			isValid: false,
			error: {
				property: "email",
				constraints: {
					"400": "L'adresse email n'est pas valide.",
				},
				children: [],
			},
		};
	}
	return { isValid: true };
}

/**
 * Validate required field
 *
 * @param value - Value to validate
 * @param fieldName - Name of the field (for error message)
 * @returns Validation result with error if invalid
 */
export function validateRequired(value: string | null | undefined, fieldName: string): ValidationResult {
	if (!value || typeof value !== "string" || value.trim().length === 0) {
		return {
			isValid: false,
			error: {
				property: fieldName,
				constraints: {
					"400": `Le champ "${fieldName}" est obligatoire.`,
				},
				children: [],
			},
		};
	}
	return { isValid: true };
}

/**
 * Validate number range
 *
 * @param value - Number value to validate (as string)
 * @param fieldName - Name of the field (for error message)
 * @param min - Minimum value (inclusive)
 * @param max - Maximum value (inclusive)
 * @param allowSpecial - Special value to allow (e.g., -1 for unlimited)
 * @returns Validation result with error if invalid
 */
export interface ValidateNumberRangeOptions {
	value: string;
	fieldName: string;
	min: number;
	max: number;
	allowSpecial?: number;
}

export function validateNumberRange(opts: ValidateNumberRangeOptions): ValidationResult {
	const { value, fieldName, min, max, allowSpecial } = opts;
	const numValue = parseInt(value, 10);
	if (isNaN(numValue)) {
		return {
			isValid: false,
			error: {
				property: fieldName,
				constraints: {
					"400": `Le champ "${fieldName}" doit être un nombre.`,
				},
				children: [],
			},
		};
	}

	if (allowSpecial !== undefined && numValue === allowSpecial) {
		return { isValid: true };
	}

	if (numValue < min || numValue > max) {
		return {
			isValid: false,
			error: {
				property: fieldName,
				constraints: {
					"400": `Le champ "${fieldName}" doit être entre ${min} et ${max}${allowSpecial !== undefined ? ` (ou ${allowSpecial} pour illimité)` : ""}.`,
				},
				children: [],
			},
		};
	}

	return { isValid: true };
}

/**
 * Validate minimum value
 *
 * @param value - Number value to validate (as string)
 * @param fieldName - Name of the field (for error message)
 * @param min - Minimum value (inclusive)
 * @returns Validation result with error if invalid
 */
export function validateMin(value: string, fieldName: string, min: number): ValidationResult {
	const numValue = parseInt(value, 10);
	if (isNaN(numValue)) {
		return {
			isValid: false,
			error: {
				property: fieldName,
				constraints: {
					"400": `Le champ "${fieldName}" doit être un nombre.`,
				},
				children: [],
			},
		};
	}

	if (numValue < min) {
		return {
			isValid: false,
			error: {
				property: fieldName,
				constraints: {
					"400": `Le champ "${fieldName}" doit être >= ${min}.`,
				},
				children: [],
			},
		};
	}

	return { isValid: true };
}

/**
 * Build validation error for custom messages
 *
 * @param fieldName - Name of the field
 * @param message - Error message
 * @param value - Optional field value
 * @returns ValidationError object
 */
export function buildValidationError(fieldName: string, message: string, value?: string): ValidationError {
	return {
		property: fieldName,
		constraints: {
			"400": message,
		},
		children: [],
		value,
	};
}
